import {
  UseMutationOptions,
  UseQueryOptions,
  mutationOptions,
  queryOptions,
} from '@tanstack/react-query';
import { shouldRetry } from '../utilities/queryHelpers';
import {
  UserStatus as AccountStatus,
  EntityType,
} from 'adc-sirius-module-common';
import mediator from '../utilities/mediator';
import log from '../utilities/log';
import {
  Account,
  AccountCreationRequest,
  AccountCreationResult,
  AccountRoleAssignmentScope,
  AccountRoleAssignmentsUpdateRequest,
  AccountUpdateRequest,
  AccountWithRoleAssignments,
  ApiErrorResponse,
} from '../models';

type AccountsQueryParams = {
  status?: AccountStatus;
};

type AccountQueryParams = {
  key?: string;
};

type CreateAccountVariables = {
  account: AccountCreationRequest;
};

type UpdateAccountVariables = {
  account: AccountUpdateRequest;
};

export type UpdateAccountRolesForScopeVariables = {
  scope: AccountRoleAssignmentScope;
  updateRequest: AccountRoleAssignmentsUpdateRequest;
};

export function accountsQueryOptions(
  params: AccountsQueryParams = {},
  options?: Partial<UseQueryOptions<Account[]>>,
) {
  return queryOptions<Account[]>({
    ...options,
    queryKey: ['accounts'],
    queryFn: async () => {
      try {
        return await mediator.request<Account[]>('account:getAccounts', {
          ...params,
        });
      } catch (error: any) {
        log.error(error);
        throw new ApiErrorResponse(error.status, error.message);
      }
    },
  });
}

export function accountQueryOptions(
  params: AccountQueryParams = {},
  options?: Partial<UseQueryOptions<Account>>,
) {
  return queryOptions<Account>({
    ...options,
    queryKey: ['account', params.key],
    queryFn: async () => {
      try {
        return await mediator.request<Account>('account:getAccountByKey', {
          ...params,
        });
      } catch (error: any) {
        log.error(error);
        throw new ApiErrorResponse(error.status, error.message);
      }
    },
    enabled: !!params.key,
  });
}

export function accountsByAssignmentScopeQueryKey(
  scope: AccountRoleAssignmentScope,
) {
  return [
    'accounts',
    'assignments',
    scope.entityType,
    scope.entityType === EntityType.System ? 'system' : scope.entityKey,
  ] as const;
}

export function accountsByAssignmentScopeQueryOptions(
  scope: AccountRoleAssignmentScope,
  options?: Partial<UseQueryOptions<AccountWithRoleAssignments[]>>,
) {
  return queryOptions<AccountWithRoleAssignments[]>({
    ...options,
    queryKey: accountsByAssignmentScopeQueryKey(scope),
    queryFn: async () => {
      try {
        return await mediator.request<AccountWithRoleAssignments[]>(
          'account:getAccountsByAssignmentScope',
          {
            scope,
          },
        );
      } catch (error: any) {
        log.error(error);
        throw new ApiErrorResponse(error.status, error.message);
      }
    },
    enabled:
      (scope.entityType === EntityType.System || !!scope.entityKey) &&
      options?.enabled !== false,
    retry: shouldRetry,
  });
}

export function createAccountMutateOptions(
  options?: Partial<
    UseMutationOptions<AccountCreationResult, Error, CreateAccountVariables>
  >,
) {
  return mutationOptions<AccountCreationResult, Error, CreateAccountVariables>({
    ...options,
    mutationFn: async ({ account }: CreateAccountVariables) => {
      try {
        return await mediator.request<AccountCreationResult>(
          'account:createAccount',
          {
            data: account,
          },
        );
      } catch (error: any) {
        log.error(error);
        throw new ApiErrorResponse(error.status, error.message, error.data);
      }
    },
  });
}

export function updateAccountMutateOptions(
  options?: Partial<UseMutationOptions<Account, Error, UpdateAccountVariables>>,
) {
  return mutationOptions<Account, Error, UpdateAccountVariables>({
    ...options,
    mutationFn: async ({ account }: UpdateAccountVariables) => {
      try {
        return await mediator.request<Account>('account:updateAccount', {
          data: account,
        });
      } catch (error: any) {
        log.error(error);
        throw new ApiErrorResponse(error.status, error.message, error.data);
      }
    },
  });
}

export function updateAccountRolesForScopeMutateOptions(
  options?: Partial<
    UseMutationOptions<
      AccountWithRoleAssignments[],
      Error,
      UpdateAccountRolesForScopeVariables
    >
  >,
) {
  return mutationOptions<
    AccountWithRoleAssignments[],
    Error,
    UpdateAccountRolesForScopeVariables
  >({
    ...options,
    mutationFn: async ({
      scope,
      updateRequest,
    }: UpdateAccountRolesForScopeVariables) => {
      try {
        return await mediator.request<AccountWithRoleAssignments[]>(
          'account:updateAccountRolesForAssignmentScope',
          {
            scope,
            data: updateRequest,
          },
        );
      } catch (error: any) {
        log.error(error);
        throw new ApiErrorResponse(error.status, error.error?.message);
      }
    },
  });
}
